import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Send,
  Upload,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  FileCheck,
  Globe,
  Mail,
  Package,
} from "lucide-react";
import { Opportunity, UserProfile } from "../../../types";
import { recordOfficialSubmission } from "../../../services/submissionService";
import { uploadRfpDocument } from "../../../services/storageService";

export interface RecordSubmissionModalProps {
  isOpen: boolean;
  onClose: () => void;
  opportunity: Opportunity;
  user: UserProfile | null;
  onSubmissionSuccess?: () => void;
}

export const RecordSubmissionModal: React.FC<RecordSubmissionModalProps> = ({
  isOpen,
  onClose,
  opportunity,
  user,
  onSubmissionSuccess,
}) => {
  const [method, setMethod] = useState<"portal" | "email" | "hardcopy" | "other">("portal");
  const [portalName, setPortalName] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsSubmitting(true);

    try {
      let receiptStoragePath: string | undefined;
      let receiptDownloadUrl: string | undefined;
      let receiptFileName: string | undefined;

      if (receiptFile) {
        const uploadResult = await uploadRfpDocument(opportunity.id, receiptFile);
        receiptStoragePath = uploadResult.storagePath;
        receiptDownloadUrl = uploadResult.downloadUrl;
        receiptFileName = uploadResult.fileName;
      }

      await recordOfficialSubmission({
        opportunityId: opportunity.id,
        submission: {
          method,
          portalName: portalName.trim() || undefined,
          confirmationNumber: confirmationNumber.trim() || undefined,
          notes: notes.trim() || undefined,
          receiptStoragePath,
          receiptDownloadUrl,
          receiptFileName,
        },
        user: {
          displayName: user?.displayName || "Proposal Lead",
          email: user?.email || "mark@mlconnections.com",
        },
        currentHistory: opportunity.stageHistory || [],
      });

      if (onSubmissionSuccess) {
        onSubmissionSuccess();
      }
      onClose();
    } catch (err) {
      console.error("Failed to record official submission:", err);
      setErrorMsg(err instanceof Error ? err.message : "Failed to record submission.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          transition={{ duration: 0.2 }}
          className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900 z-10"
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-100 pb-4 dark:border-slate-800">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-50 text-[#2ba5b5] dark:bg-teal-950/50">
                <Send className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  Record Official Submission
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {opportunity.client} — {opportunity.title}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {errorMsg && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-rose-50 p-3 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            {/* Submission Method */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                Submission Method
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { id: "portal", label: "Portal", icon: Globe },
                  { id: "email", label: "Email", icon: Mail },
                  { id: "hardcopy", label: "Hardcopy", icon: Package },
                  { id: "other", label: "Other", icon: Send },
                ].map((item) => {
                  const Icon = item.icon;
                  const isSelected = method === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setMethod(item.id as typeof method)}
                      className={`flex flex-col items-center gap-1 rounded-xl border p-2.5 text-xs font-medium transition-colors ${
                        isSelected
                          ? "border-[#2ba5b5] bg-teal-50/50 text-[#2ba5b5] dark:bg-teal-950/40"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Portal Name / URL (if portal) */}
            {method === "portal" && (
              <div>
                <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Portal Name or Website URL
                </label>
                <input
                  type="text"
                  value={portalName}
                  onChange={(e) => setPortalName(e.target.value)}
                  placeholder="e.g. Bonfire, BidNet Direct, DemandStar, Illinois Gateway"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            )}

            {/* Confirmation Number */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Confirmation Number / Tracking ID
              </label>
              <input
                type="text"
                value={confirmationNumber}
                onChange={(e) => setConfirmationNumber(e.target.value)}
                placeholder="e.g. CONF-98214-X, FedEx #789123456789"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 placeholder-slate-400 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Confirmation Receipt Upload */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Submission Confirmation Receipt (PDF, Screenshot, or Email)
              </label>
              <div className="flex items-center gap-2">
                <label className="btn-animate inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                  <Upload className="h-3.5 w-3.5 text-[#2ba5b5]" />
                  <span>Choose Receipt File</span>
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        setReceiptFile(e.target.files[0]);
                      }
                    }}
                  />
                </label>
                {receiptFile && (
                  <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium dark:text-emerald-400">
                    <FileCheck className="h-4 w-4" />
                    <span className="truncate max-w-[200px]">{receiptFile.name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Submission Notes */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Submission Notes & Dispatch Details
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="e.g. Uploaded all 3 volumes 2 hours prior to deadline. Automated confirmation email received."
                className="w-full rounded-lg border border-slate-200 bg-white p-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-[#2ba5b5] focus:outline-hidden dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            {/* Notice */}
            <div className="rounded-xl bg-teal-50/60 p-3 text-xs text-slate-600 dark:bg-teal-950/30 dark:text-slate-400 flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-[#2ba5b5] shrink-0 mt-0.5" />
              <span>
                Recording this submission will advance this proposal stage to{" "}
                <strong>Submitted</strong>, lock final baseline timestamps, and log an entry into
                the audit trail.
              </span>
            </div>

            {/* Footer Buttons */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-animate inline-flex items-center gap-1.5 rounded-lg bg-[#2ba5b5] px-4 py-2 text-xs font-bold text-white shadow-2xs hover:bg-[#238a97] disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    <span>Saving Submission...</span>
                  </>
                ) : (
                  <>
                    <Send className="h-3.5 w-3.5" />
                    <span>Confirm & Execute Submission</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
